<script setup>
import JobHeader from 'module/jobHeader/JobHeader.vue';
import TabNav from 'module/tabNav/TabNav.vue';
import AppIcon from 'module/icon/AppIcon.vue';
import JobContent from 'module/jobContent/JobContent.vue';
import MobileKeyfactBox from 'module/mobileKeyfactBox/MobileKeyfactBox.vue';
import { onMounted, ref } from 'vue';
import { VisitedJobsHistory } from 'util/client-side-storage/visitedJobsHistory';
import { onConsentChange } from 'util/consent';
import { getTrackingCategory, trackGa4 } from 'util/googleAnalytics';

const props = defineProps({
    jobDetail: {
        type: Object,
        required: true,
    },
    jobHeader: {
        type: Object,
        required: true,
    },
    jobContent: {
        type: Object,
        required: true,
    },
    isSplitview: {
        type: Boolean,
        default: false,
    },
    isSeamless: {
        type: Boolean,
        default: false,
    },
    shareModal: {
        type: Object,
    },
    isPreview: {
        type: Boolean,
    },
    active: {
        type: Boolean,
    },
    tabs: {
        type: Array,
        default: () => [],
    },
    keyfactBox: {
        type: Object,
    },
    alarmDisruptor: {
        type: Object,
    },
    jobSuggestions: {
        type: Object,
    },
    applyAction: {
        type: Object,
    },
    showKeyfactBox: {
        type: Boolean,
    },
});

defineEmits([
    /* JobHeader */
    'removeCloseTriggerStyles',
    'addCloseTriggerStyles',
    'pushState',
    'stickyHeaderVisible',

    /* JobContent */
    'jobContentLoaded',
    'jobContentLoading',
    'removeModalStyle',
    'addModalStyle',
    'openDeleteModal',
    'showSnackbar',
    'memoSaved',
    'memoDeleted',
    'showMemoOverlay',
    'hideMemoOverlay',
]);

function addJobToVisitedJobsCache() {
    if (props.jobDetail.id) {
        VisitedJobsHistory.addJob(props.jobDetail.id);
    }
}

function print() {
    window.print?.();
}

function toggleContent(tab) {
    if (tab.id === 'company'
        && !props.jobDetail.isInactive
        && props.eventCategory !== 'page: preview'
    ) {
        let productType = '';
        if (props.jobDetail.isTypeBrandingSolution) {
            productType = 'branding.solution';
        } else if (props.jobDetail.isTypeEmployerPage) {
            productType = 'employer.page';
        } else if (props.jobDetail.isTypeEmployerPagePlus) {
            productType = 'Arbeitgeberprofil';
        } else if (props.jobDetail.isTypeAgpBasic) {
            productType = 'Arbeitgeberprofil Basic';
        } else if (props.jobDetail.isTypeAgpPlus) {
            productType = 'Arbeitgeberprofil';
        }

        trackGa4({
            event: 'company_view',
            view_type: 'Employer Tab',
            product_type: productType,
            company_id: props.jobDetail.companyId ?? null,
        });
    }
}

/* Analytics */

function getTrackingDisplayFeatures() {
    const displayFeatures = [];
    if (props.jobDetail.isPushedJob) {
        displayFeatures.push('pushed_job');
    }
    if (props.jobDetail.isTypeEmployerPagePlus || props.jobDetail.isTypeBrandingSolution) {
        displayFeatures.push('insights');
    }
    if (props.jobDetail.isTypeBrandingSolution) {
        displayFeatures.push('videos');
    }
    if (props.jobDetail.hasSalaryRange) {
        displayFeatures.push('salary');
    }
    if (props.jobDetail.isHomeoffice) {
        displayFeatures.push('homeoffice');
    }

    return displayFeatures.join(', ');
}

function getApplicationTyp() {
    return props.jobDetail.isSmartApplyJob
        ? 'SmartBewerben'
        : 'External';
}

function sendShowEvents() {
    let viewType = null;
    const eventCategory = getTrackingCategory();

    if (eventCategory === 'page: job') {
        viewType = 'Standalone';
    } else if (eventCategory === 'page: jobs') {
        viewType = 'Job Search';
    } else if (eventCategory === 'page: jobalarm') {
        viewType = 'Job Alarm';
    } else if (eventCategory === 'page: jobmatching') {
        viewType = 'Job Matching';
    } else if (eventCategory === 'page: watchlist') {
        viewType = 'Job Watchlist';
    } else if (eventCategory === 'page: company-follow') {
        viewType = 'Company Alarm';
    }

    if (props.tabs) {
        if ((!props.jobDetail.isInactive) && (eventCategory !== 'page: preview')) {
            trackGa4({
                event: 'job_view',
                job_id: props.jobDetail.id ?? null,
                job_type: props.isSeamless ? 'SJA' : 'Standard',
                view_type: viewType,
                application_type: getApplicationTyp(),
                product_type: props.jobDetail.jobBundleLabel ?? null,
                display_features: getTrackingDisplayFeatures(),
                job_fields: props.jobDetail.jobFields,
                employment_types: props.jobDetail.employmentTypes,
                job_levels: props.jobDetail.jobLevel,
                company_id: props.jobDetail.isChiffreJob ? null : props.jobDetail.companyId,
            });
        }
    }
}

// TODO refactor to state-driven instead of imperative
const jobContentRef = ref(null);

function allowPointerEvents() {
    jobContentRef.value?.allowPointerEvents();
}

function deleteMemo() {
    jobContentRef.value?.deleteMemo();
}

function saveMemo() {
    jobContentRef.value?.saveMemo();
}

function memoContentSimilar() {
    return Boolean(jobContentRef.value?.memoContentSimilar());
}

function getTextareaValue() {
    return Boolean(jobContentRef.value.getTextareaValue());
}

const tabNav = ref(null);

function getActiveTab() {
    if (!tabNav.value) return false;

    return tabNav.value.getActiveTab();
}

onMounted(() => {
    onConsentChange(addJobToVisitedJobsCache);
    addJobToVisitedJobsCache();
});

defineExpose({
    allowPointerEvents,
    memoContentSimilar,
    getTextareaValue,
    getActiveTab,
    saveMemo,
    deleteMemo,
    getTrackingDisplayFeatures,
    sendShowEvents,
});
</script>

<template>
    <div
        :class="{
            'm-jobDetailContent--isSplitView': isSplitview,
        }"
        class="m-jobDetailContent"
    >
        <div
            v-if="active"
            class="m-jobDetailContent__inner"
        >
            <JobHeader
                ref="jobHeaderRef"
                :is-split-view="isSplitview"
                :job-detail="jobDetail"
                :job-header="jobHeader"
                :share-modal="shareModal"
                :apply-action="applyAction"
            />
            <MobileKeyfactBox
                v-if="showKeyfactBox"
                :company="keyfactBox.company"
                :job="keyfactBox.job"
            />
            <div class="m-jobDetailContent__tabBar">
                <div class="m-jobDetailContent__tabBarWrapper">
                    <TabNav
                        ref="tabNav"
                        :active
                        :tabs
                        @change-tab="toggleContent"
                    />
                </div>
                <button
                    v-if="!jobDetail.isInactive"
                    class="m-jobDetailContent__print"
                    data-gtm-element="JOBD: Print Job"
                    aria-label="Jobdetail drucken"
                    @click="print"
                >
                    <AppIcon
                        class="m-jobDetailContent__printIcon"
                        name="printer"
                        size="small"
                    />
                    <span
                        class="m-jobDetailContent__printLabel"
                    >Drucken</span>
                </button>
            </div>
            <JobContent
                ref="jobContentRef"
                :alarm-disruptor
                :is-preview
                :is-splitview
                :is-seamless
                :job-content
                :job-detail
                :job-suggestions
                :keyfact-box
                :tabs
                :job-bundle-label="jobDetail.jobBundleLabel"
            />
        </div>
    </div>
</template>

<style lang="scss">
@use "styles";
</style>
